import { AbstractDialog } from "./abstract_dialog.js";
import { ConfigSyncService } from "../../config_sync/config_sync_service.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { DialogId } from "./dialog_id.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { resetLinkedSocialProviders } from "./account_providers_preview_state.js";
import { performLogin } from "../../api/perform_login.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { clearFacebookAvatarCache } from "../../common/facebook_avatar_loader.js";
import { FacebookSignInPlugin } from "../../plugins/facebook_sign_in_plugin.js";
import { GoogleSignInPlugin } from "../../plugins/play_games_plugin.js";
import { ImgID } from "../../image_loader/img_id.js";
import { AppleSignInPlugin } from "../../plugins/apple_sign_in_plugin.js";
import { AppleWebSignIn } from "../../plugins/apple_web_sign_in.js";
import { DisconnectAccountNotification } from "./disconnect_account_notification.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";

export class ExitFromGameAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.EXIT_FROM_GAME_ACCOUNT_DIALOG;
  private bg!: RoundedRectangle;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 990 );
    const body_h = LayoutScaler.heightFromDesign( 776 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;
    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTitle();
    this.createCancelButton();
    this.createExitButton();
    this.createContentBg();
    this.createWarningText();
    this.createCloseIcon();
  }

  private createCancelButton(): void
  {
    const btn_x = LayoutScaler.xFromDesign( 18 );
    const btn_y = LayoutScaler.yFromDesign( 639 );
    const btn_w = LayoutScaler.widthFromDesign( 466 );
    const btn_h = LayoutScaler.heightFromDesign( 112 );

    const cancel_btn = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      LayoutScaler.cornerRadiusFromDesign( 56 ),
      "#12B2E2",
      undefined,
      true
    );

    const cancel_text = new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CANCEL",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_light_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );

    cancel_btn.addChild( cancel_text, true );
    cancel_btn.setOnClick( () => { DialogManager.closeDialog(); } );

    this.addChild( cancel_btn );
  }

  private createWarningText(): void
  {
    const text_y = LayoutScaler.yFromDesign( 195 );
    const text_w = LayoutScaler.widthFromDesign( 887 );
    const text_x = ( this.rect.getW() - text_w ) / 2;
    const text_h = LayoutScaler.heightFromDesign( 400 );

    const warning_text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "EXIT_FROM_GAME_ACCOUNT_DIALOG.WARNING",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Top,
        auto_wrap: true,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        text_style: TextStyleScheme.rubik,
        text_color: "#A85F1E",
        font_weight: 700,
      }
    );

    this.addChild( warning_text );
  }

  private createExitButton(): void
  {
    const btn_x = LayoutScaler.xFromDesign( 507 );
    const btn_y = LayoutScaler.yFromDesign( 639 );
    const btn_w = LayoutScaler.widthFromDesign( 466 );
    const btn_h = LayoutScaler.heightFromDesign( 112 );

    const exit_btn = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      LayoutScaler.cornerRadiusFromDesign( 56 ),
      "#F28519",
      undefined,
      true
    );

    const exit_text = new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        using_y_offset: false,
        auto_wrap: true,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_orange_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );

    exit_btn.addChild( exit_text, true );
    exit_btn.setOnClick( () => { void this.exitAccount(); } );

    this.addChild( exit_btn );
  }

  private is_exiting = false;

  private async exitAccount(): Promise<void>
  {
    if ( this.is_exiting ) return;
    this.is_exiting = true;

    const isNative = ( window as any ).Capacitor?.getPlatform?.() === 'ios' ||
                     ( window as any ).Capacitor?.getPlatform?.() === 'android';

    await Promise.allSettled( [
      GoogleSignInPlugin.signOut(),
      FacebookSignInPlugin.signOut(),
      ( isNative ? AppleSignInPlugin : AppleWebSignIn ).signOut(),
    ] );

    localStorage.removeItem( 'web_google_id' );
    localStorage.removeItem( 'web_facebook_id' );
    localStorage.removeItem( 'native_apple_id' );
    localStorage.removeItem( 'analytics_userid' );
    localStorage.removeItem( 'player_nickname' );
    localStorage.removeItem( 'player_avatar_id' );
    localStorage.removeItem( 'player_facebook_avatar_url' );
    clearFacebookAvatarCache();
    StatisticsStorage.remove();

    // AuthSessionStore.set( null as any );
    CumulativeBoosterService.reset();
    resetLinkedSocialProviders();

    await performLogin();

    await ConfigSyncService.refreshAll( 'account_switch' );

    DialogManager.closeAllDialogs();
    ScreenManager.showScreen( Screens.MENU );
    DialogManager.openDialog( new DisconnectAccountNotification() );
  }

  private createTitle(): void
  {
    const label_y = LayoutScaler.yFromDesign( 33 );
    const label_h = LayoutScaler.heightFromDesign( 70 );
    const label_w = this.rect.getW() * 0.9;
    const label_x = this.rect.getW2() - label_w / 2;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "EXIT_FROM_GAME_ACCOUNT_DIALOG.TITLE",
        text_color: "#DE934F",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.heightFromDesign( 50 ),
        font_weight: 700,
        auto_wrap: false,
        vertical_align: MyTextVerticalAlign.Bottom
      }
    );

    this.addChild( label );
  }

  private createBackground(): void
  {
    this.bg = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( 30 ),
        tr: LayoutScaler.cornerRadiusFromDesign( 30 ),
        br: LayoutScaler.cornerRadiusFromDesign( 70 ),
        bl: LayoutScaler.cornerRadiusFromDesign( 70 )
      },
      "#FFFFFF"
    );
    this.addChild( this.bg );
  }

  private createContentBg(): void
  {
    const bg_y = LayoutScaler.yFromDesign( 134 );
    const bg_w = LayoutScaler.widthFromDesign( 948 );
    const bg_h = LayoutScaler.heightFromDesign( 469 );
    const bg_x = ( this.rect.getW() - bg_w ) / 2;

    const content_bg = new RoundedRectangle(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      0,
      "#FFF3E8"
    );

    this.addChild( content_bg );
  }

  private createCloseIcon(): void
  {
    const icon_size = LayoutScaler.widthFromDesign( 85 );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 7 );
    const icon_y = LayoutScaler.yFromDesign( 7 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () => { DialogManager.closeDialog(); } );
    this.addChild( icon );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "ExitFromGameAccountDialog";
  }
}
